<template>
  <el-dialog 
    v-model="dialogVisible" 
    title="我的权益" 
    width="90%" 
    :style="{ maxWidth: '1100px' }"
    @close="handleClose" 
    :close-on-click-modal="false"
    class="equity-dialog"
    top="8vh"
  >
    <div class="equity-content" v-loading="loading">
      <!-- 当前权益卡片 -->
      <div class="current-equity-card" v-if="userDiscount">
        <div class="card-header">
          <div class="badge">
            <el-icon class="crown-icon"><Trophy /></el-icon>
            <span>当前权益</span>
          </div>
        </div>
        <div class="card-body">
          <div class="discount-display">
            <span class="discount-number">{{ formatDiscount(userDiscount.discount) }}</span>
            <span class="discount-unit">折</span>
          </div>
          <div class="equity-info">
            <p class="order-count">已完成 <strong>{{ orderList.length }}</strong> 单</p>
            <p class="copywriting">{{ userDiscount.copywriting }}</p>
          </div>
        </div>
      </div>

      <!-- 权益档位列表 -->
      <div class="equity-levels">
        <h3 class="section-title">
          <el-icon><Medal /></el-icon>
          <span>权益档位</span>
        </h3>
        <div class="levels-grid">
          <div 
            v-for="(gear, index) in discountGears" 
            :key="index"
            class="level-card"
            :class="{ 'active': isCurrentLevel(gear), 'reached': isReachedLevel(gear) }"
          >
            <div class="level-tag" v-if="isCurrentLevel(gear)">当前档位</div>
            <div class="level-header">
              <div class="level-icon">
                <el-icon><Star /></el-icon>
              </div>
              <div class="level-discount">{{ formatDiscount(gear.discount) }}折</div>
            </div>
            <div class="level-body">
              <div class="level-requirement">满 {{ gear.orderNum }} 单</div>
              <div class="level-desc">{{ gear.copywriting }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 历史订单 -->
      <div class="order-history">
        <h3 class="section-title">
          <el-icon><List /></el-icon>
          <span>历史订单</span>
        </h3>
        <el-table :data="orderList" style="width: 100%" class="order-table" empty-text="暂无订单记录" max-height="280">
          <el-table-column prop="orderNumber" label="订单号" min-width="180">
            <template #default="scope">
              <span class="order-number">{{ scope.row.orderNumber }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="price" label="订单金额" width="120">
            <template #default="scope">
              <span class="price">¥{{ scope.row.price }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="type" label="支付类型" width="120">
            <template #default="scope">
              <el-tag :type="getPaymentTagType(scope.row.type)" size="small">
                {{ getPaymentType(scope.row.type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="payTime" label="支付时间" min-width="180">
            <template #default="scope">
              <span class="pay-time">{{ formatTime(scope.row.payTime) }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, watch, defineProps, defineEmits } from 'vue';
import { Trophy, Medal, Star, List } from '@element-plus/icons-vue';
import { getDiscountGear, getUserDiscount, getOrderList } from '@/api/api';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits(['update:visible']);

const dialogVisible = ref(props.visible);
const loading = ref(false);
const discountGears = ref<any[]>([]);
const userDiscount = ref<any>(null);
const orderList = ref<any[]>([]);

watch(() => props.visible, (val) => {
  dialogVisible.value = val;
  if (val) {
    fetchAllData();
  }
});

const handleClose = () => {
  emit('update:visible', false);
};

// 获取所有数据
const fetchAllData = async () => {
  loading.value = true;
  try {
    await Promise.all([
      fetchDiscountGear(),
      fetchUserDiscount(),
      fetchOrderList()
    ]);
  } catch (error) {
    console.error('获取权益信息失败:', error);
  } finally {
    loading.value = false;
  }
};

// 获取权益档位列表
const fetchDiscountGear = async () => {
  try {
    const response = await getDiscountGear();
    if (response.data && response.data.code === 0) {
      discountGears.value = response.data.data || [];
    }
  } catch (error) {
    console.error('获取权益档位失败:', error);
  }
};

// 获取用户当前档位
const fetchUserDiscount = async () => {
  try {
    const response = await getUserDiscount();
    if (response.data && response.data.code === 0) {
      userDiscount.value = response.data.data;
    }
  } catch (error) {
    console.error('获取用户档位失败:', error);
  }
};

// 获取历史订单
const fetchOrderList = async () => {
  try {
    const response = await getOrderList();
    if (response.data && response.data.code === 0) {
      orderList.value = response.data.data || [];
    }
  } catch (error) {
    console.error('获取订单列表失败:', error);
  }
};

// 格式化折扣显示
const formatDiscount = (discount: number) => {
  if (!discount) return '10';
  return (discount * 10).toFixed(1);
};

// 格式化时间
const formatTime = (time: string) => {
  if (!time) return '-';
  return time;
};

// 判断是否是当前档位
const isCurrentLevel = (gear: any) => {
  return userDiscount.value && gear.orderNum === userDiscount.value.orderNum;
};

// 判断是否已达到该档位
const isReachedLevel = (gear: any) => {
  if (!userDiscount.value) return false;
  return gear.orderNum <= userDiscount.value.orderNum;
};

// 获取支付类型文本
const getPaymentType = (type: number) => {
  const typeMap: Record<number, string> = {
    0: '支付宝',
    1: '微信',
    2: '支付宝',
    3: '微信',
    99: '激活码',
  };
  return typeMap[type] || '未知';
};

// 获取支付类型标签颜色
const getPaymentTagType = (type: number) => {
  const tagTypeMap: Record<number, string> = {
    0: 'primary',
    1: 'success',
    2: 'primary',
    3: 'success',
    99: 'warning',
  };
  return tagTypeMap[type] || 'info';
};
</script>

<style scoped>
.equity-content {
  padding: 10px 0;
}

/* 当前权益卡片 */
.current-equity-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  color: #ffffff;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;
}

.current-equity-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.card-header {
  margin-bottom: 20px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.crown-icon {
  font-size: 18px;
  color: #ffd700;
}

.card-body {
  display: flex;
  align-items: center;
  gap: 32px;
  position: relative;
  z-index: 1;
}

.discount-display {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.discount-number {
  font-size: 56px;
  font-weight: 700;
  line-height: 1;
}

.discount-unit {
  font-size: 28px;
  font-weight: 600;
}

.equity-info {
  flex: 1;
}

.order-count {
  font-size: 18px;
  margin-bottom: 8px;
  opacity: 0.95;
}

.order-count strong {
  font-size: 24px;
  font-weight: 700;
  margin: 0 4px;
}

.copywriting {
  font-size: 14px;
  opacity: 0.85;
  margin: 0;
}

/* 权益档位列表 */
.equity-levels {
  margin-bottom: 32px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.section-title .el-icon {
  font-size: 20px;
  color: #667eea;
}

.levels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.level-card {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
  cursor: default;
}

.level-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.level-card.reached {
  background: #f0f7ff;
  border-color: #b3d8ff;
}

.level-card.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: #ffffff;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.level-tag {
  position: absolute;
  top: -10px;
  right: 16px;
  background: #ffd700;
  color: #333;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
}

.level-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.level-icon {
  width: 36px;
  height: 36px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.level-card.active .level-icon {
  background: rgba(255, 255, 255, 0.2);
}

.level-icon .el-icon {
  font-size: 20px;
  color: #667eea;
}

.level-card.active .level-icon .el-icon {
  color: #ffd700;
}

.level-discount {
  font-size: 24px;
  font-weight: 700;
  color: #667eea;
}

.level-card.active .level-discount {
  color: #ffffff;
}

.level-body {
  text-align: left;
}

.level-requirement {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

.level-card.active .level-requirement {
  color: #ffffff;
}

.level-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

.level-card.active .level-desc {
  color: rgba(255, 255, 255, 0.9);
}

/* 历史订单 */
.order-history {
  margin-top: 32px;
}

.order-table {
  border-radius: 8px;
  overflow: hidden;
}

:deep(.order-table .el-table__body-wrapper) {
  overflow-y: auto;
}

:deep(.order-table .el-table__body-wrapper::-webkit-scrollbar) {
  width: 8px;
  height: 8px;
}

:deep(.order-table .el-table__body-wrapper::-webkit-scrollbar-thumb) {
  background: #dcdfe6;
  border-radius: 4px;
}

:deep(.order-table .el-table__body-wrapper::-webkit-scrollbar-thumb:hover) {
  background: #c0c4cc;
}

:deep(.order-table .el-table__body-wrapper::-webkit-scrollbar-track) {
  background: #f5f7fa;
  border-radius: 4px;
}

.order-number {
  font-family: 'Consolas', 'Monaco', monospace;
  color: #667eea;
  font-weight: 500;
}

.price {
  font-size: 16px;
  font-weight: 600;
  color: #ff6b6b;
}

.pay-time {
  color: #666;
}

/* 移动端适配 */
@media (max-width: 768px) {
  :deep(.el-dialog) {
    width: 95% !important;
    margin: 5vh auto !important;
  }
  
  .current-equity-card {
    padding: 20px;
  }
  
  .card-body {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .discount-number {
    font-size: 48px;
  }
  
  .discount-unit {
    font-size: 24px;
  }
  
  .order-count {
    font-size: 16px;
  }
  
  .order-count strong {
    font-size: 20px;
  }
  
  .levels-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
  
  .level-card {
    padding: 16px;
  }
  
  .level-discount {
    font-size: 20px;
  }
  
  :deep(.el-table) {
    font-size: 12px;
  }
  
  .section-title {
    font-size: 16px;
  }
}

/* 超小屏幕 */
@media (max-width: 480px) {
  .levels-grid {
    grid-template-columns: 1fr;
  }
  
  :deep(.el-table .el-table__cell) {
    padding: 8px 4px;
  }
}
</style>
